import React, { useCallback, useEffect, useMemo } from 'react';
import { SelectChangeEvent } from '@mui/material/Select';
import { useImprovedMappingConfiguration } from '../hooks/useImprovedMappingConfiguration';
import { MappingConfigurationProvider } from './MappingConfigurationContext';
import {
  useConfigurationStore,
  useWorkflowState,
} from '../../../shared/stores/useConfigurationStore';
import {
  useSelectedOption,
  useUIStore,
} from '../../../shared/stores/useUIStore';
import {
  clearConvertedSettingsMappingHandoffIntent,
  clearLiveWizardMappingHandoffIntent,
  clearReinitiatedWizardMappingHandoffIntent,
  readConvertedSettingsMappingHandoffIntent,
  readLiveWizardMappingHandoffIntent,
  readReinitiatedWizardMappingHandoffIntent,
  readResumeIntoMappingIntent,
} from '../../../shared/lib/workflowResumeIntent';
import { useActiveDocumentInfoContext } from '../../../shared/hooks/ActiveDocumentProvider';
import {
  useProjectSetupCompleted,
  useProjectSetupHistoryStore,
} from '../../../shared/stores/useProjectSetupHistoryStore';
import { SettingsType } from '../../../shared/api/pipeex/pcfSettingsClient';
import { useMappingProfileController } from '../hooks/useMappingProfileController';
import {
  markMappingProfileProjectTrust,
  normalizeMappingProjectKey,
  readMappingProjectProfileBinding,
} from '../lib/mappingProfileProjectTrust';
import { traceMappingFlow } from '../lib/mappingTrace';
import { debugMappingPage } from '../lib/mappingPageHostBridge';
import { useHostWindowVisibility } from '../../../shared/hooks/useHostWindowVisibility';
import {
  getProfileKeyParts,
  isRevitProjectIdentityPending,
  parseMappingSettingsType,
} from '../lib/mappingPageProjectProfileUtils';
import MappingConfigurationPageContent from './MappingConfigurationPageContent';

export interface MappingConfigurationPageProps {
  activeUserId?: string | null;
  onGoBack: () => void;
  onContinue: () => void;
}

const MappingConfigurationContainer: React.FC<MappingConfigurationPageProps> = ({
  activeUserId = null,
  onGoBack,
  onContinue,
}) => {
  const mappingConfig = useImprovedMappingConfiguration();

  const {
    saveConfiguration,
    objectTypeOptions,
    handleObjectTypeChange,
    setGeneralSettings,
    setAdvancedSettings,
  } = mappingConfig;
  const boltConfig = useConfigurationStore((state) => state.boltConfiguration);
  const syncBoltAttributesToMapping = useConfigurationStore((state) => state.syncBoltAttributesToMapping);
  const syncAdditionalCategoriesToMapping = useConfigurationStore(
    (state) => state.syncAdditionalCategoriesToMapping
  );
  const markWorkflowApplied = useConfigurationStore((state) => state.saveConfiguration);
  const selectedOption = useSelectedOption();
  const boltModelingChoiceValue = boltConfig?.boltModelingChoice?.value ?? '';
  const boltBaseFamilyValue = boltConfig?.boltBaseFamilyValue?.value ?? '';
  const boltSetActiveValue = boltConfig?.boltSetActiveValue?.value ?? '';
  const boltDiaValue = boltConfig?.boltDiaValue?.value ?? '';
  const boltQuantityValue = boltConfig?.boltQuantityValue?.value ?? '';
  const boltLengthValue = boltConfig?.boltLengthValue?.value ?? '';
  const boltItemDescriptionValue = boltConfig?.boltItemDescriptionValue?.value ?? '';
  const boltMaterialValue = boltConfig?.boltMaterialValue?.value ?? '';
  const boltStandardValue = boltConfig?.boltStandardValue?.value ?? '';
  const { reinitiateWorkflow } = useWorkflowState();
  const jumpTo = useUIStore((state) => state.jumpTo);
  const activeDocumentInfo = useActiveDocumentInfoContext();
  const projectSetupCompleted = useProjectSetupCompleted(
    activeUserId,
    activeDocumentInfo.projectKey
  );
  const markProjectSetupCompleted = useProjectSetupHistoryStore(
    (state) => state.markCompleted
  );
  const [resumeIntoMappingRequested] = React.useState(() => readResumeIntoMappingIntent());
  const [liveWizardMappingHandoffRequested] = React.useState(() =>
    readLiveWizardMappingHandoffIntent()
  );
  const [reinitiatedWizardMappingHandoffRequested] = React.useState(() =>
    readReinitiatedWizardMappingHandoffIntent()
  );
  const [convertedSettingsMappingHandoffRequested] = React.useState(() =>
    readConvertedSettingsMappingHandoffIntent()
  );
  const resumeRequested = projectSetupCompleted;
  const isLiveWizardMappingHandoff =
    liveWizardMappingHandoffRequested &&
    !resumeIntoMappingRequested &&
    Boolean(selectedOption);
  const isReinitiatedWizardMappingHandoff =
    isLiveWizardMappingHandoff && reinitiatedWizardMappingHandoffRequested;
  const restoreExistingProfileOnMountRequested = !isLiveWizardMappingHandoff;
  const projectIdentityPending = isRevitProjectIdentityPending(activeDocumentInfo.projectKey);
  const dockablePaneVisible = useHostWindowVisibility();
  const restoreExistingProfileOnMount =
    dockablePaneVisible && restoreExistingProfileOnMountRequested;

  useEffect(() => {
    if (convertedSettingsMappingHandoffRequested) {
      clearConvertedSettingsMappingHandoffIntent();
    }
  }, [convertedSettingsMappingHandoffRequested]);
  const [profileTrustRevision, setProfileTrustRevision] = React.useState(0);
  const bumpProfileTrustRevision = useCallback(() => {
    setProfileTrustRevision((current) => current + 1);
  }, []);
  const projectProfileBinding = useMemo(() => {
    void profileTrustRevision;
    return readMappingProjectProfileBinding(activeUserId, activeDocumentInfo.projectKey);
  }, [activeDocumentInfo.projectKey, activeUserId, profileTrustRevision]);
  const projectPreferredProfile = useMemo(() => {
    if (!projectProfileBinding) {
      return null;
    }

    const profileKeyParts = getProfileKeyParts(projectProfileBinding.profileKey);
    return {
      name: profileKeyParts.name,
      settingsType: parseMappingSettingsType(projectProfileBinding.settingsType),
      profileKey: projectProfileBinding.profileKey,
      source: projectProfileBinding.source,
      setupReadinessPolicy: projectProfileBinding.setupReadinessPolicy,
      repairPersistence: projectProfileBinding.repairPersistence,
    };
  }, [projectProfileBinding]);
  const projectProfileSelectionRequiresLoad =
    !isLiveWizardMappingHandoff &&
    !projectIdentityPending &&
    Boolean(normalizeMappingProjectKey(activeDocumentInfo.projectKey));

  useEffect(() => {
    traceMappingFlow('project profile binding resolved', {
      projectKey: activeDocumentInfo.projectKey,
      documentName: activeDocumentInfo.documentName,
      profileKey: projectPreferredProfile?.profileKey ?? null,
      settingsType: projectPreferredProfile?.settingsType ?? null,
      source: projectProfileBinding?.source ?? null,
      repairPersistenceStatus: projectProfileBinding?.repairPersistence?.status ?? null,
    });
  }, [
    activeDocumentInfo.documentName,
    activeDocumentInfo.projectKey,
    projectPreferredProfile?.profileKey,
    projectPreferredProfile?.settingsType,
    projectProfileBinding?.repairPersistence?.status,
    projectProfileBinding?.source,
  ]);

  const handleProfileConfirmedForProject = useCallback(
    (profile: { profileKey: string; settingsType: SettingsType }) => {
      const record = markMappingProfileProjectTrust({
        userId: activeUserId,
        projectKey: activeDocumentInfo.projectKey,
        profileKey: profile.profileKey,
        settingsType: profile.settingsType,
        documentName: activeDocumentInfo.documentName,
        source: 'profile-load',
      });

      if (!record) {
        return;
      }

      markProjectSetupCompleted(activeUserId, activeDocumentInfo.projectKey);
      traceMappingFlow('profile confirmed for project', {
        projectKey: activeDocumentInfo.projectKey,
        documentName: activeDocumentInfo.documentName,
        profileKey: profile.profileKey,
        settingsType: profile.settingsType,
        source: record.source,
      });
      bumpProfileTrustRevision();
    },
    [
      activeDocumentInfo.documentName,
      activeDocumentInfo.projectKey,
      activeUserId,
      bumpProfileTrustRevision,
      markProjectSetupCompleted,
    ]
  );

  const handleFlowSnapshotPersistedForProject = useCallback(
    (profile: { profileKey: string; settingsType: SettingsType }) => {
      clearLiveWizardMappingHandoffIntent();
      clearReinitiatedWizardMappingHandoffIntent();
      markProjectSetupCompleted(activeUserId, activeDocumentInfo.projectKey);
      if (
        !isReinitiatedWizardMappingHandoff &&
        projectPreferredProfile &&
        projectPreferredProfile.profileKey !== profile.profileKey
      ) {
        traceMappingFlow('live flow snapshot preserved existing project profile binding', {
          projectKey: activeDocumentInfo.projectKey,
          documentName: activeDocumentInfo.documentName,
          savedProfileKey: profile.profileKey,
          projectPreferredProfileKey: projectPreferredProfile.profileKey,
          projectPreferredSettingsType: projectPreferredProfile.settingsType,
        });
        return;
      }

      if (
        isReinitiatedWizardMappingHandoff &&
        projectPreferredProfile &&
        projectPreferredProfile.profileKey !== profile.profileKey
      ) {
        traceMappingFlow('reinitiated live flow snapshot replacing project profile binding', {
          projectKey: activeDocumentInfo.projectKey,
          documentName: activeDocumentInfo.documentName,
          savedProfileKey: profile.profileKey,
          previousProjectPreferredProfileKey: projectPreferredProfile.profileKey,
          previousProjectPreferredSettingsType: projectPreferredProfile.settingsType,
        });
      }

      const record = markMappingProfileProjectTrust({
        userId: activeUserId,
        projectKey: activeDocumentInfo.projectKey,
        profileKey: profile.profileKey,
        settingsType: profile.settingsType,
        documentName: activeDocumentInfo.documentName,
        source: 'live-flow',
      });

      if (!record) {
        return;
      }

      traceMappingFlow('live flow snapshot confirmed for project', {
        projectKey: activeDocumentInfo.projectKey,
        documentName: activeDocumentInfo.documentName,
        profileKey: profile.profileKey,
        settingsType: profile.settingsType,
        source: record.source,
      });
      bumpProfileTrustRevision();
    },
    [
      activeDocumentInfo.documentName,
      activeDocumentInfo.projectKey,
      activeUserId,
      bumpProfileTrustRevision,
      isReinitiatedWizardMappingHandoff,
      markProjectSetupCompleted,
      projectPreferredProfile,
    ]
  );

  useEffect(() => {
    debugMappingPage('[MappingConfigurationPage] resume-into-mapping intent state', {
      resumeIntoMappingRequested,
      storedIntent: readResumeIntoMappingIntent(),
    });
  }, [resumeIntoMappingRequested]);

  useEffect(() => {
    debugMappingPage('[MappingConfigurationPage] restoreExistingProfileOnMount decision', {
      resumeRequested,
      projectSetupCompleted,
      resumeIntoMappingRequested,
      liveWizardMappingHandoffRequested,
      reinitiatedWizardMappingHandoffRequested,
      isLiveWizardMappingHandoff,
      isReinitiatedWizardMappingHandoff,
      restoreExistingProfileOnMountRequested,
      restoreExistingProfileOnMount,
      dockablePaneVisible,
    });
  }, [
    dockablePaneVisible,
    isLiveWizardMappingHandoff,
    isReinitiatedWizardMappingHandoff,
    liveWizardMappingHandoffRequested,
    projectSetupCompleted,
    reinitiatedWizardMappingHandoffRequested,
    restoreExistingProfileOnMount,
    restoreExistingProfileOnMountRequested,
    resumeIntoMappingRequested,
    resumeRequested,
  ]);
  const selectObjectType = useCallback(
    (name: string) => {
      handleObjectTypeChange({ target: { value: name } } as SelectChangeEvent<string>);
    },
    [handleObjectTypeChange]
  );

  const profileController = useMappingProfileController({
    activeUserId,
    activeProjectKey: activeDocumentInfo.projectKey,
    activeDocument: activeDocumentInfo,
    saveConfiguration,
    setGeneralSettings,
    setAdvancedSettings,
    markWorkflowApplied,
    onContinue,
    reinitiateWorkflow,
    jumpTo,
    restoreExistingProfileOnMount,
    enableFlowSnapshot: isLiveWizardMappingHandoff,
    profileActivationReady: !projectIdentityPending,
    requireExplicitProfileSelection: projectProfileSelectionRequiresLoad,
    activateProfileOnSelection: !projectProfileSelectionRequiresLoad,
    projectPreferredProfile,
    deferProjectPreferredRestore: true,
    onProfileConfirmed: handleProfileConfirmedForProject,
    onFlowSnapshotPersisted: handleFlowSnapshotPersistedForProject,
  });

  useEffect(() => {
    debugMappingPage('[MappingConfigurationPage] flow selections sync to mapping start', {
      selectedOption,
      boltModelingChoice: boltModelingChoiceValue,
      boltBaseFamilyValue,
      boltSetActiveValue,
      boltDiaValue,
      boltQuantityValue,
      boltLengthValue,
      boltItemDescriptionValue,
      boltMaterialValue,
      boltStandardValue,
    });
    syncBoltAttributesToMapping({ markDirty: false });

    if (selectedOption === 'Iso Drawings Generation') {
      syncAdditionalCategoriesToMapping({ markDirty: false });
    }
    debugMappingPage('[MappingConfigurationPage] flow selections sync to mapping complete', {
      selectedOption,
      additionalCategoriesSynced: selectedOption === 'Iso Drawings Generation',
    });
  }, [
    boltBaseFamilyValue,
    boltDiaValue,
    boltItemDescriptionValue,
    boltLengthValue,
    boltMaterialValue,
    boltModelingChoiceValue,
    boltQuantityValue,
    boltSetActiveValue,
    boltStandardValue,
    syncBoltAttributesToMapping,
    syncAdditionalCategoriesToMapping,
    selectedOption,
  ]);

  return (
    <MappingConfigurationProvider value={mappingConfig}>
      <MappingConfigurationPageContent
        activeUserId={activeUserId}
        onGoBack={onGoBack}
        objectTypeOptions={objectTypeOptions}
        setGeneralSettings={setGeneralSettings}
        selectObjectType={selectObjectType}
        workflowSetupCompleted={resumeRequested}
        isLiveWizardMappingHandoff={isLiveWizardMappingHandoff}
        skipSetupReadiness={convertedSettingsMappingHandoffRequested}
        projectPreferredProfile={projectPreferredProfile}
        projectIdentityPending={projectIdentityPending}
        dockablePaneVisible={dockablePaneVisible}
        profileTrustRevision={profileTrustRevision}
        onReinitiateWorkflow={profileController.handleReinitiateWorkflow}
        {...profileController}
      />
      {profileController.profileDeleteConfirmationDialog}
    </MappingConfigurationProvider>
  );
};

export default MappingConfigurationContainer;
